'use client'

import { useSession } from 'next-auth/react'
import { createContext, PropsWithChildren, useContext, useEffect, useState } from 'react'
import { up } from 'up-fetch'

type Context = {
  upFetch: UpFetchType
}

type UpFetchType = ReturnType<typeof up<typeof fetch>>

const UpFetchContext = createContext<Context>({} as Context)

export const UpFetchProvider = ({
  accessToken,
  children,
}: PropsWithChildren<{ accessToken?: string }>) => {
  const clientSession = useSession()

  const [localAccessToken, setLocalAccessToken] = useState(accessToken)

  useEffect(() => {
    if (clientSession?.data?.user?.access_token) {
      setLocalAccessToken(clientSession.data?.user.access_token)
    }
  }, [clientSession?.data?.user?.access_token])

  const value = {
    upFetch: up(fetch, () => ({
      baseUrl: process.env.NEXT_PUBLIC_API_URL,
      headers: {
        Accept: 'application/json',
        'App-source': process.env.NEXT_PUBLIC_SOURCE,
        Authorization: localAccessToken ? `Bearer ${localAccessToken}` : undefined,
      },
    })),
  }

  return <UpFetchContext.Provider value={value}>{children}</UpFetchContext.Provider>
}

export const useUpFetch = () => {
  const context = useContext(UpFetchContext)

  if (!context) {
    throw new Error('useUpFetch must be used within a UpFetchContextProvider')
  }

  return context.upFetch
}
